/**
 * Inventario Kommo — planilla: filtros en encabezados de columna.
 * @module-isolated analytics-leads-inventario
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { KpiGrid, LoadingState, SectionHeader } from '../../../shared/components/index.js';
import { fetchContactsInventory } from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import ContactsInventarioTable from '../components/ContactsInventarioTable.jsx';

const CREADO_PRESETS = [
  { id: 'all', label: 'Todos' },
  { id: '7d', label: 'Semana' },
  { id: '14d', label: '2 semanas' },
  { id: '30d', label: '30 días' },
  { id: 'month', label: 'Mes' },
  { id: 'custom', label: 'Rango' }
];

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

function resolveCreadoRange(preset, customFrom, customTo) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  if (preset === 'all') {
    return { applyDate: false, dateFrom: '', dateTo: '' };
  }
  if (preset === 'custom') {
    if (!customFrom || !customTo) {
      return {
        applyDate: false,
        dateFrom: customFrom || '',
        dateTo: customTo || '',
        incomplete: true
      };
    }
    return { applyDate: true, dateFrom: customFrom, dateTo: customTo };
  }
  if (preset === '7d') {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { applyDate: true, dateFrom: ymdLocal(from), dateTo: ymdLocal(today) };
  }
  if (preset === '14d') {
    const from = new Date(today);
    from.setDate(from.getDate() - 13);
    return { applyDate: true, dateFrom: ymdLocal(from), dateTo: ymdLocal(today) };
  }
  if (preset === '30d') {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return { applyDate: true, dateFrom: ymdLocal(from), dateTo: ymdLocal(today) };
  }
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { applyDate: true, dateFrom: ymdLocal(from), dateTo: ymdLocal(today) };
  }
  return { applyDate: false, dateFrom: '', dateTo: '' };
}

export default function LeadsInventarioPage() {
  const [vinculo, setVinculo] = useState('all');
  const [hasLeads, setHasLeads] = useState('all');
  const [hasGic, setHasGic] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [creadoPreset, setCreadoPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const tagsParam = useMemo(
    () => (Array.isArray(selectedTags) && selectedTags.length ? selectedTags : []),
    [selectedTags]
  );

  const creadoRange = useMemo(
    () => resolveCreadoRange(creadoPreset, customFrom, customTo),
    [creadoPreset, customFrom, customTo]
  );

  const loadContacts = useCallback(async () => {
    if (creadoRange.incomplete) return;
    setBusy(true);
    setError('');
    try {
      const token = await getIdToken(false);
      const res = await fetchContactsInventory(
        {
          dateFrom: creadoRange.applyDate ? creadoRange.dateFrom : '',
          dateTo: creadoRange.applyDate ? creadoRange.dateTo : '',
          applyDate: creadoRange.applyDate ? '1' : '0',
          q: qApplied,
          vinculo,
          hasLeads,
          hasGic,
          tags: tagsParam,
          sortBy,
          sortDir,
          limit: 500,
          offset
        },
        token
      );
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudo cargar contactos.');
      }
      setPayload(res);
    } catch (err) {
      setError((err && err.message) || 'Error al cargar contactos.');
      setPayload(null);
    } finally {
      setBusy(false);
    }
  }, [
    creadoRange,
    qApplied,
    vinculo,
    hasLeads,
    hasGic,
    tagsParam,
    sortBy,
    sortDir,
    offset
  ]);

  useAnalyticsRefresh(loadContacts);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    setOffset(0);
  }, [creadoPreset, customFrom, customTo, vinculo, hasLeads, hasGic, tagsParam, qApplied]);

  function submitSearch(ev) {
    if (ev && ev.preventDefault) ev.preventDefault();
    setQApplied(String(q || '').trim());
    setOffset(0);
  }

  function handleSort(key) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'name' || key === 'email' || key === 'contactId' ? 'asc' : 'desc');
    }
    setOffset(0);
  }

  const totals = (payload && payload.totals) || null;
  const table = (payload && payload.table) || { columns: [], rows: [] };
  const matched = totals ? totals.matched : table.total || 0;
  const rows = table.rows || [];
  const canPrev = offset > 0;
  const canNext = offset + rows.length < matched;

  const tagOptions = useMemo(() => {
    const raw =
      (payload && payload.catalogs && Array.isArray(payload.catalogs.tags) && payload.catalogs.tags) ||
      [];
    return raw.map((t) => ({
      id: String(t.id || t.label || ''),
      label: String(t.label || t.id || ''),
      count: t.count,
      order: t.order != null ? t.order : 0,
      group: t.group || (t.isNoTag ? 'Especial' : 'Etiquetas'),
      isMeta: null
    }));
  }, [payload]);

  const columns =
    table.columns && table.columns.length
      ? table.columns
      : [
          { key: 'contactId', label: 'Nro Kommo', format: 'text' },
          { key: 'name', label: 'Nombre' },
          { key: 'phone', label: 'Teléfono', format: 'text' },
          { key: 'tags', label: 'Etiquetas', format: 'text' },
          { key: 'email', label: 'Email', format: 'text' },
          { key: 'leadsCount', label: 'Leads', align: 'right' },
          { key: 'gicClientName', label: 'Cliente GIC' },
          { key: 'vinculo', label: 'Vínculo' },
          { key: 'createdAt', label: 'Creado' }
        ];

  return (
    <section className="analytics-section">
      <SectionHeader
        title="Contactos Kommo"
        note="Planilla: cada título de columna ordena; Etiquetas / Leads / Cliente GIC / Vínculo / Creado filtran desde el encabezado."
      />

      <form className="analytics-lead-probe-form" onSubmit={submitSearch} style={{ marginBottom: 12 }}>
        <input
          type="search"
          className="analytics-input analytics-lead-probe-input"
          placeholder="Buscar por nombre, teléfono o nro Kommo…"
          value={q}
          disabled={busy}
          onChange={(ev) => setQ(ev.target.value)}
          aria-label="Buscar contactos"
        />
        <button type="submit" className="analytics-btn" disabled={busy}>
          Buscar
        </button>
        <button
          type="button"
          className="analytics-btn analytics-btn-ghost"
          disabled={busy}
          onClick={() => loadContacts()}
        >
          {busy ? 'Actualizando…' : 'Actualizar'}
        </button>
      </form>

      {error ? <p className="analytics-error-inline">{error}</p> : null}

      {busy && !payload ? (
        <LoadingState message="Cargando contactos Kommo…" />
      ) : (
        <>
          {payload && Array.isArray(payload.kpis) && payload.kpis.length ? (
            <div style={{ marginBottom: 16 }}>
              <KpiGrid items={payload.kpis} />
            </div>
          ) : null}

          <div
            className="analytics-toolbar"
            style={{ justifyContent: 'space-between', marginBottom: 8 }}
          >
            <span className="analytics-sub">
              {rows.length ? offset + 1 : 0}–{offset + rows.length} de {matched}
              {payload && payload.applyDate && payload.dateFrom
                ? ` · creado ${payload.dateFrom} → ${payload.dateTo}`
                : ' · creado: todos'}
              {creadoRange.incomplete ? ' · completá fechas personalizadas' : ''}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="analytics-btn analytics-btn-ghost"
                disabled={!canPrev || busy}
                onClick={() => setOffset(Math.max(0, offset - 500))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="analytics-btn analytics-btn-ghost"
                disabled={!canNext || busy}
                onClick={() => setOffset(offset + 500)}
              >
                Siguiente
              </button>
            </div>
          </div>

          <ContactsInventarioTable
            columns={columns}
            rows={creadoRange.incomplete ? [] : rows}
            emptyText={
              creadoRange.incomplete
                ? 'Completá Desde/Hasta en la columna Creado.'
                : 'Sin contactos para este filtro.'
            }
            sortKey={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            disabled={busy}
            filters={{
              tags: {
                options: tagOptions,
                value: selectedTags,
                onChange: setSelectedTags
              },
              hasLeads: { value: hasLeads, onChange: setHasLeads },
              hasGic: { value: hasGic, onChange: setHasGic },
              vinculo: { value: vinculo, onChange: setVinculo },
              creado: {
                preset: creadoPreset,
                presets: CREADO_PRESETS,
                customFrom,
                customTo,
                onPreset: setCreadoPreset,
                onFrom: setCustomFrom,
                onTo: setCustomTo
              }
            }}
          />
        </>
      )}
    </section>
  );
}
